import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs, nomenclature as nomenclatureApi } from '@/api/catalogs';
import { productDisassemblies, type ProductDisassemblyListItem } from '@/api/productDisassemblies';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const PERMISSION = 'production.assembly.manage';

export default function ProductDisassembliesPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['product-disassemblies', orgId],
    queryFn: () =>
      productDisassemblies.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const nomQ = useQuery({
    queryKey: ['nomenclature-all'],
    queryFn: () => nomenclatureApi.list({ limit: 1000 }),
  });
  const nomName = useMemo(() => {
    const m = new Map<string, string>();
    nomQ.data?.items.forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nomQ.data]);

  const cols = useMemo<ColumnDef<ProductDisassemblyListItem>[]>(
    () => [
      {
        key: 'document_number',
        header: 'Номер',
        width: 150,
        importBadge: true,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата',
        width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) =>
          r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      {
        key: 'disassembled_nomenclature_id',
        header: 'Набір',
        sortValue: (r) => nomName.get(r.disassembled_nomenclature_id) ?? '',
        cell: (r) => nomName.get(r.disassembled_nomenclature_id) ?? '',
      },
      {
        key: 'disassembled_quantity',
        header: 'К-сть',
        width: 110,
        align: 'right',
        sortValue: (r) => Number(r.disassembled_quantity),
        cell: (r) => r.disassembled_quantity,
      },
      {
        key: 'total_cost',
        header: 'Собівартість',
        width: 150,
        align: 'right',
        sortValue: (r) => Number(r.total_cost ?? 0),
        cell: (r) =>
          r.total_cost
            ? Number(r.total_cost).toLocaleString('uk-UA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '',
      },
      {
        key: 'posted',
        header: 'Стан',
        width: 150,
        sortValue: (r) => (r.state === 'CANCELLED' ? 2 : r.posted ? 1 : 0),
        cell: (r) => {
          if (r.state === 'CANCELLED') return '✕ Скасовано';
          return r.posted ? '✓ Проведено' : 'Чернетка';
        },
      },
    ],
    [nomName],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав створювати Розукомплектацію у цій юрособі (${PERMISSION})`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Розукомплектація номенклатури</h2>
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.code} — {o.name}
            </option>
          ))}
        </select>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/product-disassemblies/new')}
        >
          + Нова розукомплектація
        </button>
      </div>
      <DataTable<ProductDisassemblyListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/product-disassemblies/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
